import { Box, Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import { useDrag } from "react-use-gesture";

interface FilterModelessProps {
  open: boolean;
  onClose: () => void;
  position: { x: number; y: number };
  setPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  containerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  onDock?: () => void;
  title?: string;
  width?: number;
  height?: number;
  scrollY?: boolean;
}

export default function FilterModeless({
  open,
  onClose,
  position,
  setPosition,
  containerRef,
  children,
  onDock,
  title = "이미지 필터",
  width = 380,
  height = 500,
  scrollY = false,
}: FilterModelessProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const bind = useDrag(({ offset }) => {
    if (containerRef.current) {
      const newX = Math.max(0, offset[0]);
      const newY = Math.max(0, offset[1]);
      setPosition({ x: newX, y: newY });
    }
  });

  if (!open) return null;

  return (
    <Box
      ref={panelRef}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${width}px`,
        height: `${height}px`,
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        border: "0.5px solid var(--dk-border, rgba(0,0,0,0.13))",
        boxShadow: "0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.07)",
        overflow: "hidden",
        // ponytail: 브라우저 기본 리사이즈 핸들(우하단). 크기 저장이 필요해지면 ResizeObserver로 상태 반영
        resize: "both",
        minWidth: 240,
        minHeight: 160,
        zIndex: 1200,
        bgcolor: "var(--dk-surface, #ffffff)",
      }}
    >
      <Box
        {...bind()}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          height: "36px",
          flexShrink: 0,
          bgcolor: "var(--dk-surface-2, #f5f5f5)",
          borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
          cursor: "move",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <Tooltip title="닫기" placement="top" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{
                width: 11,
                height: 11,
                borderRadius: "50%",
                bgcolor: "#ff5f57",
                border: "0.5px solid var(--dk-border, rgba(0,0,0,0.15))",
                cursor: "pointer",
                transition: "filter 0.1s",
                "&:hover": { filter: "brightness(0.88)" },
              }}
            />
          </Tooltip>

          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: "50%",
              visibility: "hidden",
            }}
          />

          {onDock && (
            <Tooltip title="패널에 도킹" placement="top" arrow>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onDock();
                }}
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  bgcolor: "#28c840",
                  border: "0.5px solid var(--dk-border, rgba(0,0,0,0.15))",
                  cursor: "pointer",
                  transition: "filter 0.1s",
                  "&:hover": { filter: "brightness(0.88)" },
                }}
              />
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            textAlign: "center",
            fontSize: "11px",
            fontWeight: 500,
            color: "#777",
            letterSpacing: "0.02em",
            pointerEvents: "none",
          }}
        >
          {title}
        </Box>

        <Box sx={{ width: 40 }} />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowX: "hidden", overflowY: scrollY ? "auto" : "hidden" }}>{children}</Box>
    </Box>
  );
}
